import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { records, rag, redaction } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { ArrowLeft, Globe, Sparkles, FileText, Calendar, Building, Tag, CheckCircle, Loader, Eye, Shield } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_STYLES = {
  published: 'bg-green-50 text-green-700 border-green-200',
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
  archived: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function RecordDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [redacting, setRedacting] = useState(false);
  const [redactionData, setRedactionData] = useState(null);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [applying, setApplying] = useState(false);
  const [showRedaction, setShowRedaction] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [previewChecked, setPreviewChecked] = useState(false);
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || '';

  useEffect(() => {
    records.get(id)
      .then(r => setRecord(r.data))
      .catch(() => toast.error('Record not found'))
      .finally(() => setLoading(false));
  }, [id]);

  // Check if file is accessible before showing iframe
  useEffect(() => {
    if (!record?.file_path) return;
    const url = `${apiBase}/uploads/${record.file_path}`;
    fetch(url, { method: 'HEAD' })
      .then(res => { setPreviewFailed(!res.ok); setPreviewChecked(true); })
      .catch(() => { setPreviewFailed(true); setPreviewChecked(true); });
  }, [record, apiBase]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const updated = record.status === 'published'
        ? await records.unpublish(id)
        : await records.publish(id);
      setRecord(updated.data);
      toast.success(record.status === 'published' ? 'Unpublished' : 'Published to public portal');
    } catch { toast.error('Action failed'); }
    finally { setPublishing(false); }
  };

  const handleIngest = async () => {
    setIngesting(true);
    try {
      const { data } = await rag.ingest(id);
      toast.success(`Indexed ${data.embedded} chunks for semantic search`);
      setRecord(r => ({ ...r, metadata: { ...r.metadata, rag_indexed: true, chunk_count: data.embedded } }));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Indexing failed');
    } finally { setIngesting(false); }
  };

  const handleAnalyze = async () => {
    setRedacting(true);
    setShowRedaction(true);
    try {
      const { data } = await redaction.analyze(id);
      setRedactionData(data);
      setSelectedEntities(data.entities.map((_, i) => i));
      toast.success(`Found ${data.entities.length} items to review`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Redaction analysis failed');
      setShowRedaction(false);
    } finally {
      setRedacting(false);
    }
  };

  const handleApply = async () => {
    if (!redactionData) return;
    setApplying(true);
    try {
      const entitiesToApply = selectedEntities.map(i => redactionData.entities[i]);
      const { data } = await redaction.apply(redactionData.job_id, entitiesToApply);
      toast.success('Redacted version created successfully');
      setShowRedaction(false);
      setRedactionData(null);
      setRecord(r => ({
        ...r,
        metadata: { ...r.metadata, has_redacted_version: true, redacted_record_id: data.redacted_record.id }
      }));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to apply redactions');
    } finally {
      setApplying(false);
    }
  };

  const toggleEntity = (index) => {
    setSelectedEntities(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const fileUrl = record ? `${apiBase}/uploads/${record.file_path}` : null;
  const isPDF = record?.mime_type === 'application/pdf' || record?.file_name?.endsWith('.pdf');
  // Browsers can't natively render TIFF or BMP — exclude them from image preview
  const fileName = record?.file_name?.toLowerCase() || '';
  const isUnrenderable = /\.(tif|tiff|bmp)$/i.test(fileName) ||
    record?.mime_type === 'image/tiff' ||
    record?.mime_type === 'image/bmp';
  const isImage = !isUnrenderable && record?.mime_type?.startsWith('image/');
  const isRAGIndexed = record?.metadata?.rag_indexed;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader size={20} className="animate-spin text-gray-300" />
    </div>
  );

  if (!record) return (
    <div className="p-6 text-center text-sm text-gray-400">
      Record not found. <Link to="/records" className="text-blue-600">Back to records</Link>
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Back + actions */}
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors">
          <ArrowLeft size={15} /> Back
        </button>
        <div className="flex items-center gap-2">
          {(user?.role === 'admin' || user?.role === 'staff') && (
            <>
              <button onClick={handleIngest} disabled={ingesting}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all
                  ${isRAGIndexed
                    ? 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                {ingesting
                  ? <Loader size={12} className="animate-spin" />
                  : isRAGIndexed ? <CheckCircle size={12} /> : <Sparkles size={12} />}
                {ingesting ? 'Indexing...' : isRAGIndexed ? `RAG indexed (${record.metadata?.chunk_count || 0} chunks)` : 'Index for RAG'}
              </button>
              <button onClick={handleAnalyze} disabled={redacting || !record.ocr_text}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all bg-white text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40">
                {redacting
                  ? <Loader size={12} className="animate-spin" />
                  : <Shield size={12} />}
                {redacting ? 'Analyzing...' : 'Redact for public release'}
              </button>
              <button onClick={handlePublish} disabled={publishing}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all
                  ${record.status === 'published'
                    ? 'bg-green-50 text-green-700 border-green-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200'}`}>
                <Globe size={12} />
                {publishing ? '...' : record.status === 'published' ? 'Published — click to unpublish' : 'Publish to public portal'}
              </button>
            </>
          )}
          {fileUrl && (
            <a href={fileUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors">
              <Eye size={12} /> View file
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Metadata panel */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-start justify-between gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <FileText size={18} className="text-blue-500" />
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_STYLES[record.status] || STATUS_STYLES.draft}`}>
                {record.status}
              </span>
            </div>
            <h1 className="text-base font-semibold text-gray-900 leading-snug mb-4">{record.title}</h1>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <FileText size={13} className="text-gray-300 flex-shrink-0" />
                <span className="text-gray-500">{record.document_type}</span>
              </div>
              {record.department && (
                <div className="flex items-center gap-2 text-sm">
                  <Building size={13} className="text-gray-300 flex-shrink-0" />
                  <span className="text-gray-500">{record.department}</span>
                </div>
              )}
              {record.record_date && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar size={13} className="text-gray-300 flex-shrink-0" />
                  <span className="text-gray-500">{format(new Date(record.record_date), 'MMMM d, yyyy')}</span>
                </div>
              )}
              {record.tags?.length > 0 && (
                <div className="flex items-start gap-2">
                  <Tag size={13} className="text-gray-300 flex-shrink-0 mt-0.5" />
                  <div className="flex flex-wrap gap-1">
                    {record.tags.map(tag => (
                      <span key={tag} className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full border border-gray-100">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {record.description && (
              <div className="mt-4 pt-4 border-t border-gray-50">
                <div className="text-xs font-medium text-gray-400 mb-1.5">Description</div>
                <p className="text-sm text-gray-600 leading-relaxed">{record.description}</p>
              </div>
            )}
          </div>

          {/* File info */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="text-xs font-medium text-gray-400 mb-3">File info</div>
            <div className="space-y-2 text-xs text-gray-500">
              <div className="flex justify-between"><span>Filename</span><span className="font-mono text-gray-700 truncate ml-2 max-w-32">{record.file_name}</span></div>
              <div className="flex justify-between"><span>Size</span><span>{record.file_size ? `${(record.file_size / 1e3).toFixed(0)} KB` : '—'}</span></div>
              <div className="flex justify-between"><span>Uploaded</span><span>{format(new Date(record.created_at), 'MMM d, yyyy')}</span></div>
              {record.published_at && <div className="flex justify-between"><span>Published</span><span>{format(new Date(record.published_at), 'MMM d, yyyy')}</span></div>}
            </div>
          </div>

          {/* OCR status */}
          {record.ocr_text && (
            <div className="bg-teal-50 rounded-xl border border-teal-100 p-4">
              <div className="flex items-center gap-2 text-xs text-teal-700 font-medium mb-1">
                <CheckCircle size={12} /> OCR text extracted
              </div>
              <p className="text-xs text-teal-600 line-clamp-4 leading-relaxed">{record.ocr_text.slice(0, 200)}...</p>
            </div>
          )}
        </div>

        {/* Preview panel */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden h-full min-h-96">
            {isPDF && fileUrl && previewChecked && !previewFailed ? (
              <iframe src={fileUrl} title={record.title} className="w-full h-full min-h-[600px]" style={{ border: 'none' }} />
            ) : isImage && fileUrl ? (
              <div className="p-4 flex items-center justify-center h-full">
                <img src={fileUrl} alt={record.title} className="max-w-full max-h-[600px] object-contain rounded-lg" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-64 p-8 text-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                  <FileText size={24} className="text-gray-300" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">{previewFailed ? 'Preview not available' : record.file_name}</div>
                  <div className="text-xs text-gray-400 mb-4">{previewFailed ? 'View the extracted text below or use View file button above' : (record.mime_type || 'Unknown format')}</div>
                  {!previewFailed && fileUrl && (
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                      <Eye size={14} /> Open file
                    </a>
                  )}
                </div>
                {record.ocr_text && (
                  <div className="w-full mt-4 text-left">
                    <div className="text-xs font-medium text-gray-400 mb-2">Extracted text content</div>
                    <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600 leading-relaxed max-h-80 overflow-y-auto whitespace-pre-wrap font-mono">
                      {record.ocr_text}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Redaction Panel */}
      {showRedaction && (
        <div className="mt-5 bg-white rounded-xl border border-red-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-red-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-red-500" />
              <h2 className="text-sm font-semibold text-gray-900">AI Redaction Review</h2>
              {redactionData && (
                <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">
                  {redactionData.entities.length} items detected
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {redactionData && !applying && (
                <button onClick={handleApply} disabled={selectedEntities.length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-40">
                  <CheckCircle size={12} />
                  Apply {selectedEntities.length} redaction{selectedEntities.length !== 1 ? 's' : ''}
                </button>
              )}
              {applying && (
                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Loader size={12} className="animate-spin" /> Creating redacted version...
                </span>
              )}
              <button onClick={() => { setShowRedaction(false); setRedactionData(null); }}
                className="text-xs text-gray-400 hover:text-gray-600">
                Close
              </button>
            </div>
          </div>

          {redacting && (
            <div className="p-8 text-center">
              <Loader size={20} className="animate-spin text-red-400 mx-auto mb-3" />
              <p className="text-sm text-gray-500">AI is analyzing document for sensitive content...</p>
              <p className="text-xs text-gray-400 mt-1">This may take 10-30 seconds</p>
            </div>
          )}

          {redactionData && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
              {/* Entity list */}
              <div className="p-4 max-h-96 overflow-y-auto">
                <div className="text-xs font-medium text-gray-400 mb-3">Detected entities</div>
                {redactionData.entities.length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-4">
                    No PII detected in this document.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {redactionData.entities.map((entity, idx) => (
                      <label key={idx}
                        className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors text-xs
                          ${selectedEntities.includes(idx) ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-100'}`}>
                        <input type="checkbox" checked={selectedEntities.includes(idx)}
                          onChange={() => toggleEntity(idx)}
                          className="mt-0.5 rounded border-gray-300 text-red-600 focus:ring-red-500" />
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-gray-800 truncate">"{entity.text}"</div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs">
                              {entity.category.replace(/_/g, ' ')}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-xs
                              ${entity.confidence === 'high' ? 'bg-red-100 text-red-700'
                                : entity.confidence === 'medium' ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-600'}`}>
                              {entity.confidence}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Highlighted text preview */}
              <div className="lg:col-span-2 p-4">
                <div className="text-xs font-medium text-gray-400 mb-3">Document preview with highlights</div>
                <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600 leading-relaxed max-h-96 overflow-y-auto whitespace-pre-wrap font-mono">
                  {highlightEntities(redactionData.ocr_text, redactionData.entities, selectedEntities)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function highlightEntities(text, entities, selectedIndices) {
  if (!entities || entities.length === 0) return text;

  const highlights = [];
  entities.forEach((entity, idx) => {
    if (!selectedIndices.includes(idx)) return;
    let pos = 0;
    while ((pos = text.indexOf(entity.text, pos)) !== -1) {
      highlights.push({ start: pos, end: pos + entity.text.length, entity, idx });
      pos += entity.text.length;
    }
  });

  highlights.sort((a, b) => a.start - b.start);

  // Remove overlaps
  const cleaned = [];
  for (const h of highlights) {
    if (cleaned.length === 0 || h.start >= cleaned[cleaned.length - 1].end) {
      cleaned.push(h);
    }
  }

  const parts = [];
  let cursor = 0;
  cleaned.forEach((h, i) => {
    if (h.start > cursor) {
      parts.push(<span key={`t-${i}`}>{text.slice(cursor, h.start)}</span>);
    }
    parts.push(
      <mark key={`h-${i}`} className="bg-red-200 text-red-900 px-0.5 rounded"
        title={`${h.entity.category} (${h.entity.confidence})`}>
        {text.slice(h.start, h.end)}
      </mark>
    );
    cursor = h.end;
  });
  if (cursor < text.length) {
    parts.push(<span key="tail">{text.slice(cursor)}</span>);
  }
  return parts;
}
